import type { BackendAxiosError } from "services/api/backendError";
import {
  getBackendErrorMessage,
  getBackendValidationErrorMessage,
} from "services/api/backendError";
import type { StaffPaymentMethodType } from "services/api/company/staff.service";
import {
  isIbanChecksumFailure,
  toPaymentMethodFallbackErrorTranslationKey,
} from "../../../shared/staff.paymentMethod.logic";
import { toStaffBackendErrorTranslationKey } from "../../../shared/staffBackendError.logic";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

interface ResolveStaffProfilePanelErrorMessageContext {
  paymentMethodType?: StaffPaymentMethodType;
}

const DELETE_WORKER_MESSAGE_TO_ALERT_KEY: Record<string, string> = {
  "Worker not found": "Staff.ProfilePanelDeleteErrorWorkerNotFound",
  "Can not delete worker with time reports":
    "Staff.ProfilePanelDeleteErrorHasTimeReports",
  "Can not delete registered worker":
    "Staff.ProfilePanelDeleteErrorRegisteredWorker",
  "Worker can not be edited": "Staff.ProfilePanelErrorWorkerCanNotBeEdited",
};

/**
 * Handles resolve staff profile panel error key.
 */
export const resolveStaffProfilePanelErrorKey = (
  error: BackendAxiosError,
): string => {
  return (
    toStaffBackendErrorTranslationKey(getBackendErrorMessage(error)) ??
    "Alerts.SomethingWentWrong"
  );
};

/**
 * Handles resolve delete worker error key.
 */
export const resolveDeleteWorkerErrorKey = (
  error: BackendAxiosError | null | undefined,
): string => {
  const backendMessage = getBackendErrorMessage(error);
  if (!backendMessage) return "Alerts.SomethingWentWrong";
  const mappedKey = DELETE_WORKER_MESSAGE_TO_ALERT_KEY[backendMessage];
  if (mappedKey) return mappedKey;
  return "Alerts.SomethingWentWrong";
};

/**
 * Handles resolve staff profile panel error message.
 */
export const resolveStaffProfilePanelErrorMessage = (
  error: BackendAxiosError,
  translate: TranslateFn,
  context?: ResolveStaffProfilePanelErrorMessageContext,
): string => {
  const validationMessage = getBackendValidationErrorMessage({
    field: "Request.Value",
    payload: error.response?.data,
  });
  if (
    context?.paymentMethodType === "InternationalBankAccount" &&
    isIbanChecksumFailure(validationMessage)
  ) {
    return translate(
      "Staff.PaymentMethodInternationalBankAccountChecksumInvalid",
    );
  }

  const backendMessage = getBackendErrorMessage(error);
  if (
    backendMessage === "Invalid payment method value" &&
    context?.paymentMethodType != null
  ) {
    return translate(
      toPaymentMethodFallbackErrorTranslationKey(context.paymentMethodType),
    );
  }

  return translate(resolveStaffProfilePanelErrorKey(error));
};
